import { useCallback, useRef, useState } from 'react'
import { CanonicalLoad } from '@numeo/types-core'
import { searchLoadsStreaming } from 'features/load-hub/api/loads.api'
import { buildRequest, EMPTY_FILTERS } from './requestMapping'

interface BoxTruckLoadLookup {
    load: CanonicalLoad | null
    loading: boolean
    notFound: boolean
    /** Fetch the SYLECTUS_EMAIL load with this Numeo id and expose it on `load`. */
    lookup: (numeoId: string) => void
    /** Abort any in-flight lookup and reset. */
    clear: () => void
}

/**
 * One-shot fetch of a single box-truck (SYLECTUS_EMAIL) load by its Numeo id — the SAME find-by-Numeo-ID
 * global lookup the board's filter uses (a `details->>'numeoId'` server match). Powers opening the load-detail
 * drawer from a bid notification, where only the bid is known: its externalId deterministically derives the
 * load's Numeo id (see displayNumeoLoadId), which equals the stored id, so this reliably resolves the load.
 * A new lookup aborts the previous; `clear()` aborts and resets.
 */
export const useBoxTruckLoadLookup = (): BoxTruckLoadLookup => {
    const [load, setLoad] = useState<CanonicalLoad | null>(null)
    const [loading, setLoading] = useState(false)
    const [notFound, setNotFound] = useState(false)
    const abortRef = useRef<AbortController | null>(null)

    const clear = useCallback(() => {
        abortRef.current?.abort()
        abortRef.current = null
        setLoad(null)
        setLoading(false)
        setNotFound(false)
    }, [])

    const lookup = useCallback((numeoId: string) => {
        abortRef.current?.abort()
        const controller = new AbortController()
        abortRef.current = controller
        setLoad(null)
        setNotFound(false)
        setLoading(true)
        let found: CanonicalLoad | null = null
        // No supplements: the find-by-Numeo-ID request early-returns to SYLECTUS_EMAIL-only anyway.
        void searchLoadsStreaming(buildRequest({ ...EMPTY_FILTERS, numeoId }, 0, { dat: false }), {
            signal: controller.signal,
            // The find-by-Numeo-ID request is SYLECTUS_EMAIL-only and matches at most one load; take the first row.
            onSourceLoads: (_source, incoming) => {
                if (controller.signal.aborted || found || incoming.length === 0) return
                found = incoming[0]
            },
            onSourceError: () => {},
            onComplete: () => {
                if (controller.signal.aborted) return
                setLoad(found)
                setLoading(false)
                setNotFound(!found)
            },
        }).catch(() => {
            if (controller.signal.aborted) return
            setLoading(false)
            setNotFound(true)
        })
    }, [])

    return { load, loading, notFound, lookup, clear }
}
